import buffer from '@turf/buffer'
import envelope from '@turf/envelope'
import { featureCollection } from '@turf/helpers'
import pointOnFeature from '@turf/point-on-feature'
import $ from 'jquery'
import { isNumber } from 'lodash'
import get from 'lodash/get'
import isArray from 'lodash/isArray'
import isNil from 'lodash/isNil'
import memoize from 'lodash/memoize'
import OLGeometry from 'ol/geom/Geometry'
import OLGeometryCollection from 'ol/geom/GeometryCollection'
import OLGeomLineString from 'ol/geom/LineString'
import { fromExtent as OLPolygonFromExtent } from 'ol/geom/Polygon'
import { transform as OLProjTransform, transformExtent as OLProjTransformExtent } from 'ol/proj'

import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { ShowWhatsHere } from 'drawer/tabs/info/WhatsHereUtils'
import { getMap } from 'utils/MapUtils'
import { targetGetCenter } from 'utils/target'

export const DEFAULT_ZOOM_FOR_POINT = 16

const PoziZoom = memoize(function () {
  // Adapters
  const adapters = {
    zoomToExtent: function (extent, preventZoomIn, callback) {
      return zoomToExtent(extent, preventZoomIn, callback)
    },
    zoomToFeatures: function (olFeatures, olLayer, callback) {
      return zoomToFeatures(olFeatures, olLayer, callback)
    },
    zoomToTarget: function (xy, preventZoomIn, callback) {
      return zoomToTarget(xy, preventZoomIn, callback)
    },
    zoomMap: function (geojson, zoomPadding, bSelectCenterPoint, resolve, reject, callback) {
      return zoomMap(geojson, zoomPadding, bSelectCenterPoint, resolve, reject, callback)
    },
    zoomToGeoJSON: function (geojson, zoomPadding, bSelectCenterPoint, callback) {
      return zoomToGeoJSON(geojson, zoomPadding, bSelectCenterPoint, callback)
    },
    zoomPanTo: function (olGeom, preventZoomIn, callback) {
      return zoomPanTo(olGeom, preventZoomIn, callback)
    },
    centerOnTarget: function () {
      return centerOnTarget()
    }
  }

  const app = PoziUtils.getApp()

  /**
   * Zoom map to extent
   */
  const zoomToExtent = function (extent, preventZoomIn = false, callback) {
    console.debug('zoomToExtent', extent, preventZoomIn)

    if (!extent) {
      console.log('zoomToExtent- no coords found.')
      return
    }

    if (!(extent instanceof Array) || extent.length !== 4) {
      console.log('zoomToExtent - invalid extent', extent)
      return
    }

    // Make sure that the coordinates are numbers, as they sometimes get returned from the server
    // as strings
    try {
      extent = extent.map(coordinate => Number(coordinate))
    } catch (error) {
      console.log('zoomToExtent - cannot convert coordinates to number. Expect problemns', extent)
    }

    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: 'extent',
      action: 'Zoom to'
    })

    HistoryUpdate()

    const transformedExtent = OLProjTransformExtent(extent, 'EPSG:4326', 'EPSG:3857')
    const extentPolygon = OLPolygonFromExtent(transformedExtent)

    zoomPanTo(extentPolygon, preventZoomIn, callback)
  }

  const getGeometriesFromOlFeatures = function (olFeatures = []) {
    console.debug('getGeometriesFromOlFeatures', olFeatures)

    // Extract the geometries from the features
    const olGeometries = []
    for (const olFeature of olFeatures) {
      const olGeometry = olFeature.getGeometry?.()
      if (olGeometry) {
        olGeometries.push(olGeometry)
      }
    }

    console.debug('getGeometriesFromOlFeatures - olGeometries', olGeometries)

    return olGeometries
  }

  const getZoomForOlGeometries = (olGeometries, olLayer, keepCurrentZoomWherePossible = false) => {
    // console.debug('getZoomForOlFeatures', olGeometries, olLayer, keepCurrentZoomWherePossible)

    const map = getMap()
    if (!map) {
      console.log('getZoomForOlFeatures - map not initialised yet.')
      return
    }

    // Handle case where no geometry is found
    if (!olGeometries.length) {
      console.log('getZoomForOlFeatures - no geometries found')
      return
    }

    const currentView = map.getView()
    const currentResolution = currentView.getResolution() || 0
    const currentZoom = Math.round(currentView.getZoom())

    // Calculate the extent of the combined geometries and the corresponding resolution and zoom level
    const combinedGeometries = new OLGeometryCollection(olGeometries)
    const combinedGeometriesExtent = combinedGeometries.getExtent()
    const combinedGeometriesResolution = currentView.getResolutionForExtent(combinedGeometriesExtent)
    const combinedGeometriesZoom = Math.round(currentView.getZoomForResolution(combinedGeometriesResolution))

    // If the feature is a single point, the geometry zoom level is DEFAULT_ZOOM_FOR_POINT. Otherwise, we use the
    // calculated zoom level for the extent of all geometries.
    const geomIsSinglePoint = olGeometries.length === 1 && !!olGeometries[0]?.getType().match(/point/i)
    const zoomFromGeometries = geomIsSinglePoint ? DEFAULT_ZOOM_FOR_POINT : combinedGeometriesZoom

    // If keepCurrentZoomWherePossible is set, we aim use the current zoom level unless the layer has a max and min
    //  resolution, where we may need to adjust the zoom level based on that.
    // If not set, we use the zoom level for the extent of all geometries with a special case for single points.
    let zoom = keepCurrentZoomWherePossible ? currentZoom : zoomFromGeometries

    // Check if the layer has a max and min resolution. We may need to adjust the zoom level based on that.
    const { maxResolution = Infinity, minResolution = 0 } = olLayer?.getProperties() || {}

    const layerIsScaleDependent = maxResolution < Infinity || minResolution > 0
    if (!layerIsScaleDependent) {
      return zoom
    }

    const zoomForMinResolution = Math.floor(currentView.getZoomForResolution(minResolution))
    const zoomForMaxResolution = Math.ceil(currentView.getZoomForResolution(maxResolution))

    // We will need to zoom out if the current map resolution is less than the min resolution or
    // zoom in if the current resolution is greater than the max resolution
    if (currentResolution < minResolution) {
      console.debug('getZoomForOlFeatures - need to zoom out', currentZoom, zoomForMinResolution)
      zoom = zoomForMinResolution
    } else if (currentResolution > maxResolution) {
      console.debug('getZoomForOlFeatures - need to zoom in', currentZoom, zoomForMaxResolution)
      zoom = zoomForMaxResolution
    }

    // console.debug('getZoomForOlFeatures - zoom', zoom)

    return zoom
  }

  const zoomToFeatures = (olFeatures, options) => {
    const { callback = null, keepCurrentZoomWherePossible = false, olLayer = null } = options
    let { zoom } = options

    console.debug('zoomToFeatures', olFeatures, options)

    if (!olFeatures?.length) {
      console.log('zoomToFeatures - no features provided', olFeatures)
      return
    }

    // Extract the geometries from the features
    const olGeometries = getGeometriesFromOlFeatures(olFeatures)

    // Handle case where no geometry is found
    if (!olGeometries.length) {
      console.log('zoomToFeatures - no geometries found')
      return
    }

    // If no zoom level is provided, calculate the zoom level based on the geometries and the layer
    if (!zoom) {
      zoom = getZoomForOlGeometries(olGeometries, olLayer, keepCurrentZoomWherePossible)
    }

    // console.log('zoomToFeatures - zoom', zoom)

    // If the feature is a single point, zoom to that point, otherwise calculate the extent of
    // all the features and zoom to that
    const geometryIsSinglePoint = olGeometries.length === 1 &&
        !!olGeometries[0]?.getType().match(/point/i)

    // console.debug('zoomToFeatures - geomIsSinglePoint?', geomIsSinglePoint)

    let geomToZoomTo
    if (geometryIsSinglePoint) {
      geomToZoomTo = olGeometries[0]
    } else {
      const combinedGeometry = new OLGeometryCollection(olGeometries)
      const combinedGeometryBoundingBox = OLPolygonFromExtent(combinedGeometry.getExtent())
      geomToZoomTo = combinedGeometryBoundingBox
    }

    // Call the zoom function
    zoomPanTo(geomToZoomTo, zoom, callback)
  }

  /**
   * Zoom map to lng,lat and set target state.
   */
  const zoomToTarget = function (xy, preventZoomIn = false, callback) {
    console.debug('zoomToTarget', xy, preventZoomIn)

    if (!xy || xy.length <= 0) {
      console.error('No coords found.')
      return
    }

    if (xy.length < 2) {
      console.error('Invalid coordinates', xy)
      return
    }

    console.debug('zoomToTarget', xy)

    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: 'target',
      action: 'Zoom to'
    })

    // Remove target
    HistoryUpdate()

    // Make sure that the coordinates are numbers, as they sometimes get returned from the server
    // as strings
    try {
      xy = xy.map(coordinate => Number(coordinate))
    } catch (error) {
      console.log('zoomToTarget - cannot convert coordinates to number. Expect problemns', xy)
    }

    // console.debug("Option selected:"+xy);
    const [lng, lat] = xy
    const diagonal = new OLGeomLineString(
      $.map([[lng - 0.001, lat - 0.001], [lng + 0.001, lat + 0.001]], function (pt) {
        return [OLProjTransform(pt, 'EPSG:4326', 'EPSG:3857')]
      })
    )

    zoomPanTo(diagonal, preventZoomIn, callback)
  }

  /**
   * Zoom on geojson
   */
  const zoomToGeoJSON = function (geojson, zoomPadding, bSelectCenterPoint, callback) {
    console.debug('zoomToGeoJSON')
    return new Promise(function (resolve, reject) {
      return zoomMap(geojson, zoomPadding, bSelectCenterPoint, resolve, reject, callback)
    })
  }

  const zoomMap = function (geojson, zoomPadding = 0, bSelectCenterPoint, resolve, reject, callback) {
    console.debug('zoomMap')

    let message = ''
    if (!geojson || !geojson.features || geojson.features.length === 0) {
      message = 'PoziZoom: Unable to find any features in geojson'
      return reject(message)
    }

    const isSinglePoint = geojson.features.length === 1 && geojson.features[0]?.geometry?.type === 'Point'

    console.debug('zoomMap - geojson is point?', isSinglePoint)

    if (isSinglePoint) {
      const xy = geojson.features[0].geometry.coordinates
      zoomToTarget(xy, false, callback)
    } else {
      const enveloped = envelope(geojson)
      console.debug('zoomMap - enveloped', enveloped)
      const cornerA = enveloped.geometry.coordinates[0][0]
      const cornerB = enveloped.geometry.coordinates[0][2]

      const buffered = buffer(geojson, -0.0001, { units: 'kilometers' })

      console.debug('zoomMap - buffered', buffered)

      const pointOnSurface = pointOnFeature(buffered)

      // const centerOfMass = centerOfMass(geojson)
      // const midpoint = midpoint(pointOnSurface, centerOfMass)
      geojson.xy = pointOnSurface.geometry.coordinates

      const diagonal = new OLGeomLineString(
        $.map([[cornerA[0] - zoomPadding, cornerA[1] - zoomPadding], [cornerB[0] + zoomPadding, cornerB[1] + zoomPadding]], function (pt) {
          return [OLProjTransform(pt, 'EPSG:4326', 'EPSG:3857')]
        })
      )

      zoomPanTo(diagonal, false, callback)
    }

    if (bSelectCenterPoint) {
      const multiPointFeatures = []
      for (const key in geojson.features) {
        const feature = geojson.features[key]
        const buffered2 = buffer(feature, -0.0001, { units: 'kilometers' }) || feature
        const featureOnSurface2 = pointOnFeature(buffered2)
        multiPointFeatures.push(featureOnSurface2)
      }
      const fc = featureCollection(multiPointFeatures)
      ShowWhatsHere(fc)

      // TODO: select marker should move to it's own module rather than global app object.
      // Add green dot where the address point is and trigger property retrieval code.
      // app.selectMarker.setPosition(app.transformFrom4326(geojson.xy)) // Triggers app.selectMarker.on('change:position' ...
      // PoziMap().showMarker()
    }

    if (geojson.features.length > 1) {
      PoziUtils.gtag({
        category: 'useraction',
        status: 'success',
        identifier: 'map',
        action: 'Zoom to',
        additionalInfo: 'multi location'
      })
    } else {
      PoziUtils.gtag({
        category: 'useraction',
        status: 'success',
        identifier: 'map',
        action: 'Zoom to',
        additionalInfo: 'single location'
      })
    }

    resolve('PoziZoom: Complete.')
  }

  // Animated zoom / panning
  const zoomPanTo = function (olGeom, zoom, callback) {
    console.debug('zoomPanTo', olGeom, zoom)

    if (typeof callback !== 'function') {
      callback = (success) => console.debug('zoomPanTo - zoom completed (default callback)', success)
    }

    const map = getMap()
    const view = map?.getView()

    if (!view) {
      console.log('zoomPanTo - view not available (yet?)...')

      return
    }

    // Fallback to map extent
    if (!olGeom) {
      olGeom = map?.getSize()
    }

    // Screen resolution-sensitive padding
    // https://openlayers.org/en/latest/apidoc/ol.View.html#fit

    // Special case where 'preventZoomIn' is a boolean. If set to true, don't zoom in.
    // If the zoom is a number, use that instead and otherwise, fallback to default zoom levels.
    if (zoom === true) {
      zoom = view.getZoom()
    } else if (!isNumber(zoom)) {
      // Default to zoom level DEFAULT_ZOOM_FOR_POINT if geometry is a point, or 20 otherwise
      const geomIsPoint = olGeom instanceof OLGeometry && olGeom.getType() === 'Point'
      zoom = geomIsPoint ? DEFAULT_ZOOM_FOR_POINT : 20
    }

    // Add padding for a nicer UX
    const currentWindowMode = Model.get('app.event.window.mode', '')
    const base = currentWindowMode === 'mobile' ? 5 : 75
    const padding = [base, base, base, base]

    // Fitting the map view to the passed extent
    view.fit(olGeom, {
      padding,
      maxZoom: zoom, // When a zoom/pan is requested.
      duration: 1000, // in ms
      callback
    })
  }

  // posEnum: upper, middle (default), lower
  const centerOnTarget = function () {
    console.debug('centerOnTarget')

    const view = app.map.getView()
    const args = {}
    const center = targetGetCenter()
    if (isNil(center)) {
      return
    }
    const centerCoords = get(center, 'geometry.coordinates', null)
    if (!isArray(centerCoords)) {
      return
    }
    args.center = OLProjTransform(centerCoords, 'EPSG:4326', 'EPSG:3857')
    view.animate(args) // animate is less jaring than setCenter.
  }

  // Return adapters (must be at end of adapter)
  return adapters
})

export default PoziZoom
